/**
 * Viewer inbox API — unified heads-up dashboard stream.
 *
 * Backed by vingapi `GET /api/viewer/inbox/`. One card per share, sorted by
 * most-recent invitation (todo) or most-recent completion (done). Filtered
 * universally to active shares.
 */

import type { FileRef } from '@/types/file';
import { apiClient } from './client';

export type InboxMode = 'todo' | 'done';
export type InboxTypeFilter = 'all' | 'packet' | 'checklist';
export type InboxContentType = 'packet' | 'checklist';

/** Minimal sender identity, surfaced for message attribution. */
export interface InboxInvitationSender {
  id: number;
  /** Display name (first + last), trimmed; may be empty if the user has no name set. */
  name: string;
}

/**
 * One invitation this viewer received for a share.
 *
 * Multiple of these stack on a single InboxItem when the share has been sent
 * more than once (original + reminders, successive resends). Ordered
 * newest-first by `sentAt`.
 */
export interface InboxInvitationMessage {
  /** When this viewer was sent this invitation (Recipient.sent). ISO string. */
  sentAt: string;
  /** Custom message body the sender wrote. Null if no custom note was included. */
  message: string | null;
  /** True if this send was a reminder, not the original invitation. */
  isReminder: boolean;
  /** Who sent this invitation. */
  sender: InboxInvitationSender;
}

/**
 * One inbox card.
 *
 * Wire format is snake_case (matches vingapi), but apiClient transforms
 * keys to camelCase on the way in — so the runtime shape this app sees
 * is camelCase. Keep this in mind when writing MSW handlers: they emit
 * snake_case JSON that apiClient then transforms.
 */
export interface InboxItem {
  shareId: number;
  /** Share URL slug — click target is `/view/{stub}`. */
  stub: string;
  contentType: InboxContentType;
  title: string;
  /**
   * Sort key timestamp.
   *   - todo: MAX(Recipient.sent)
   *   - done: COALESCE(Assignment.date_completed, MAX(Submission.submitted_date))
   */
  primaryDate: string;
  /**
   * Newest-first list of invitations this viewer received for this share.
   * One entry per send (original + reminders + resends). Always present;
   * may be empty for done-mode cards where the viewer was never on a
   * Recipient row (e.g. assignment created without an invitation send).
   */
  invitations: InboxInvitationMessage[];

  // Packet-only — null/absent on checklist cards.
  progress?: number | null;
  dateDue?: string | null;
  consumeMinutes?: number | null;
  consumeInterval?: number | null;
  score?: number | null;
  certificate?: FileRef | null;
}

export interface InboxResponse {
  items: InboxItem[];
  /** Opaque cursor for the next page. null when at end of stream. */
  nextCursor: string | null;
  /**
   * True when this viewer has at least one completed row (the Done list with
   * type=all and no search). Server-decided and independent of mode, type, q
   * and cursor; the client never infers it (vingapi#1858 S3).
   */
  hasCompleted: boolean;
}

/**
 * Query keys for the viewer inbox.
 *
 * Rooted here, next to the API contract, because two features share them:
 * the viewer dashboard reads `list(...)`, and the consume flow invalidates
 * `all` when the viewer completes content (a completion moves the share
 * from To Do to Done, so every cached inbox page is stale).
 */
export const viewerInboxKeys = {
  all: ['viewer', 'inbox'] as const,
  list: (mode: InboxMode, type: InboxTypeFilter, q: string) =>
    [...viewerInboxKeys.all, mode, type, q] as const,
};

export interface InboxParams {
  mode?: InboxMode;
  type?: InboxTypeFilter;
  cursor?: string;
  limit?: number;
  /** Case-insensitive substring filter on packet/checklist title. */
  q?: string;
}

/**
 * Fetch one page of the viewer's unified inbox.
 *
 * GET /api/viewer/inbox/
 */
export async function getViewerInbox(params: InboxParams = {}): Promise<InboxResponse> {
  const response = await apiClient.get<InboxResponse>('/api/viewer/inbox/', { params });
  return response.data;
}
